import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API } from "@/lib/constants";
import { Lightbulb, Loader2, Eye, Compass, HelpCircle, ShieldCheck, Ban, MessageSquarePlus } from "lucide-react";

// ITEM 26 — the recommendation layer. ONE recommendation per relationship, in Remi's shape.
// The hard part is refusing: where the dimensions can't support a move, Rex says so and names
// the capture that would unlock one — never a confident sentence built on nothing.

const openCapture = (clientId, seedText) =>
  window.dispatchEvent(new CustomEvent("rex:open-capture", { detail: { clientId, seedText } }));

const Row = ({ icon: Icon, label, children, tone = "text-slate-600" }) => (
  <div className="flex items-start gap-2.5">
    <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${tone}`} />
    <div className="text-sm">
      <span className="font-medium text-slate-500">{label}: </span>
      <span className="text-slate-800">{children}</span>
    </div>
  </div>
);

function SupportedCard({ r }) {
  const rec = r.recommendation;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`rec-supported-${r.client_id}`}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <Link to={`/clients/${r.client_id}`} className="text-base font-semibold text-slate-900 hover:underline" data-testid="rec-client-link">
          {r.client_name}
        </Link>
        <div className="flex items-center gap-2">
          {r.restricted && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-medium text-white">
              <Ban className="h-3 w-3" /> Restricted
            </span>
          )}
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${r.confidence === "high" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
                data-testid="rec-confidence">
            {r.confidence} confidence
          </span>
        </div>
      </div>

      <div className="space-y-2">
        <Row icon={Eye} label="Observed" tone="text-slate-400">{rec.observed}</Row>
        <Row icon={HelpCircle} label="Likely means" tone="text-slate-400">{rec.likely_meaning}</Row>
        <div className="my-3 rounded-lg bg-indigo-50 px-3 py-2.5" data-testid="rec-action">
          <Row icon={Compass} label="Do" tone="text-indigo-600">
            <span className="font-semibold text-indigo-900">{rec.action}</span>
          </Row>
        </div>
        <Row icon={Lightbulb} label="Why" tone="text-slate-400">{rec.why}</Row>
        <Row icon={Compass} label="When" tone="text-slate-400">{rec.when}</Row>
        <Row icon={Ban} label="Avoid" tone="text-rose-400">{rec.avoid}</Row>
        <Row icon={ShieldCheck} label="You'll know it's working when" tone="text-emerald-500">{rec.progress_signal}</Row>
      </div>

      {r.evidence?.length > 0 && (
        <div className="mt-4 border-t border-slate-100 pt-3" data-testid="rec-evidence">
          <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Grounded in</div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {r.evidence.map((e, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-600">
                {e.dimension}: <span className="font-medium text-slate-800">{typeof e.value === "object" ? e.value.name : e.value}</span>
                {e.confirmed
                  ? <span className="text-emerald-600" title="Human-confirmed">✓</span>
                  : <span className="text-amber-500" title="Inferred, not yet confirmed">~</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center">
        <button onClick={() => openCapture(r.client_id, "")}
          className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          data-testid="rec-capture-btn">
          <MessageSquarePlus className="h-3.5 w-3.5" /> Capture what happened
        </button>
      </div>
    </div>
  );
}

function InsufficientCard({ r }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4" data-testid={`rec-insufficient-${r.client_id}`}>
      <div className="flex items-center justify-between gap-3">
        <Link to={`/clients/${r.client_id}`} className="text-sm font-semibold text-slate-800 hover:underline">
          {r.client_name}
        </Link>
        <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">Not enough signal yet</span>
      </div>
      <p className="mt-1.5 text-xs text-slate-500">{r.observed}</p>
      <p className="mt-1 text-xs text-slate-600"><span className="font-medium">To unlock:</span> {r.unlock}</p>
      <div className="mt-2 flex items-center">
        <button onClick={() => openCapture(r.client_id, "")}
          className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
          data-testid="rec-unlock-capture-btn">
          <MessageSquarePlus className="h-3.5 w-3.5" /> Capture now
        </button>
      </div>
    </div>
  );
}

export default function Recommendations() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/relationship/recommendations`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div className="mx-auto max-w-3xl px-6 py-8" data-testid="recommendations-page">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-lg bg-indigo-50 p-2 text-indigo-600"><Lightbulb className="h-5 w-5" /></div>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Recommendations</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            One next move per relationship — what's observed, what it likely means, what to do, and
            how you'll know it's working. Where the signal can't support one, Rex says so instead of guessing.
          </p>
        </div>
      </div>

      {loading && (
        <div className="mt-10 flex items-center gap-2 text-sm text-slate-400" data-testid="rec-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Reading the relationships…
        </div>
      )}

      {!loading && data && (
        <>
          <div className="mt-5 flex items-center gap-4 text-xs text-slate-500" data-testid="rec-summary">
            <span><span className="font-semibold text-slate-900">{data.counts.supported}</span> with a recommendation</span>
            <span className="text-slate-300">•</span>
            <span>{data.counts.insufficient} need a capture first</span>
          </div>

          {data.supported.length > 0 && (
            <div className="mt-6 space-y-4" data-testid="rec-supported-list">
              {data.supported.map((r) => <SupportedCard key={r.client_id} r={r} />)}
            </div>
          )}

          {data.supported.length === 0 && (
            <div className="mt-6 rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center" data-testid="rec-none-supported">
              <p className="text-sm font-medium text-slate-700">No relationship has enough confirmed signal for a recommendation yet.</p>
              <p className="mt-1 text-sm text-slate-500">
                This isn't broken — it's waiting for input. Recommendations come from confirmed dimensions,
                and dimensions only exist once someone has told Rex what happened. Capture a conversation on any
                relationship below and Rex will have something to say about it.
              </p>
            </div>
          )}

          {data.insufficient.length > 0 && (
            <div className="mt-10" data-testid="rec-insufficient-section">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Need a capture first ({data.insufficient.length})</h2>
              <div className="mt-4 space-y-3">
                {data.insufficient.map((r) => <InsufficientCard key={r.client_id} r={r} />)}
              </div>
            </div>
          )}

          <p className="mt-8 rounded-lg bg-indigo-50 px-4 py-3 text-xs text-indigo-800" data-testid="rec-principle">
            {data.principle}
          </p>
        </>
      )}
    </div>
  );
}
